/**
 * Product-description grader.
 *
 * A pure, deterministic, heuristic scorer over a pasted product
 * description. No LLM, no network: the same input always yields the
 * same score, which makes it safe to run on an unauthenticated public
 * endpoint (or directly in the browser) and consistent with every
 * other free tool on the site.
 *
 * The grader returns:
 *   - an overall 0-100 score,
 *   - a per-dimension breakdown (each 0-100 with a short verdict),
 *   - concrete flagged issues the writer can act on,
 *   - a structured rewrite SCAFFOLD built from the writer's own words
 *     (hook + benefit bullets + spec block + trust line + CTA).
 *
 * The rewrite is explicitly a structuring/clean-up of the existing
 * copy. It does NOT invent specs, numbers or claims, and the UI is
 * honest that Idukki's AI can take it further. Do not describe the
 * scaffold as "AI-generated".
 */

export type DimensionId =
  | 'substance'
  | 'specificity'
  | 'benefits'
  | 'sensory'
  | 'trust'
  | 'scannability'
  | 'readability'
  | 'filler'
  | 'cta'

export type DimensionScore = {
  id: DimensionId
  label: string
  /** 0-100 sub-score. */
  score: number
  /** Weight this dimension carries toward the overall score. */
  weight: number
  /** One short, plain verdict on this dimension. */
  verdict: string
}

export type GraderIssue = {
  /** Severity drives the UI ordering + colour. */
  severity: 'high' | 'medium' | 'low'
  text: string
}

export type RewriteScaffold = {
  hook: string
  bullets: string[]
  specs: string[]
  trustLine: string
  cta: string
}

export type GraderResult = {
  /** 0-100 overall, weighted across the dimensions. */
  score: number
  band: 'weak' | 'fair' | 'good' | 'strong'
  wordCount: number
  sentenceCount: number
  dimensions: DimensionScore[]
  issues: GraderIssue[]
  suggestions: string[]
  rewrite: RewriteScaffold
}

// ─── Word lists ────────────────────────────────────────────────────

// Filler / AI-tell phrases that add no information. Penalised hard:
// they are the loudest signal of generated or lazy copy.
const FILLER_PATTERNS: { rx: RegExp; label: string }[] = [
  { rx: /\bseamless(ly)?\b/gi, label: 'seamless' },
  { rx: /\bgame[- ]?changer\b/gi, label: 'game-changer' },
  { rx: /\brevolution(ize|ise|ary)\b/gi, label: 'revolutionary' },
  { rx: /\bcutting[- ]edge\b/gi, label: 'cutting-edge' },
  { rx: /\bstate[- ]of[- ]the[- ]art\b/gi, label: 'state-of-the-art' },
  { rx: /\bin today'?s\b/gi, label: "in today's …" },
  { rx: /\bworld[- ]class\b/gi, label: 'world-class' },
  { rx: /\bbest[- ]in[- ]class\b/gi, label: 'best-in-class' },
  { rx: /\bunparalleled\b/gi, label: 'unparalleled' },
  { rx: /\bone[- ]of[- ]a[- ]kind\b/gi, label: 'one-of-a-kind' },
  { rx: /\bele(vate|vates|vating)\b/gi, label: 'elevate' },
  { rx: /\bunlock\b/gi, label: 'unlock' },
  { rx: /\bsupercharge\b/gi, label: 'supercharge' },
  { rx: /\bnext[- ]level\b/gi, label: 'next-level' },
  { rx: /\bmust[- ]have\b/gi, label: 'must-have' },
  { rx: /\bperfect for everyone\b/gi, label: 'perfect for everyone' },
  { rx: /\bsay goodbye to\b/gi, label: 'say goodbye to' },
  { rx: /\blook no further\b/gi, label: 'look no further' },
  { rx: /\bwhether you'?re\b/gi, label: "whether you're …" },
  { rx: /\bpremium quality\b/gi, label: 'premium quality (unproven)' },
  { rx: /\bhigh[- ]quality\b/gi, label: 'high-quality (unproven)' },
  { rx: /\btop[- ]notch\b/gi, label: 'top-notch' },
]

// Words that mark a benefit / outcome rather than a feature. Used to
// reward copy that translates specs into what the buyer gets.
const BENEFIT_MARKERS = [
  'so you', 'so that', 'helps you', 'lets you', 'keeps you', 'means you',
  'so your', 'gives you', 'saves you', 'no more', 'without', 'stays',
  'lasts', 'fits', 'feels', 'all day', 'every day', 'for years',
]

// Sensory + use-case language: how the thing looks, feels, sounds,
// and where/when it gets used.
const SENSORY_MARKERS = [
  'soft', 'smooth', 'crisp', 'warm', 'cool', 'light', 'heavy', 'sturdy',
  'plush', 'matte', 'glossy', 'textured', 'breathable', 'quiet', 'bright',
  'rich', 'velvety', 'buttery', 'cosy', 'cozy', 'feels like', 'looks like',
  'morning', 'evening', 'commute', 'gym', 'office', 'travel', 'weekend',
  'kitchen', 'outdoors', 'at home', 'on the go', 'wear it', 'use it', 'pair it',
]

// Trust / social-proof hooks.
const TRUST_MARKERS = [
  'review', 'reviews', 'rated', 'rating', 'customers', 'thousands',
  'loved by', 'trusted', 'bestsell', 'guarantee', 'warranty', 'return',
  'money-back', 'verified', 'as seen', 'award', 'recommend', 'tested',
  'certified', 'star', '5-star', 'five-star',
]

// Spec / specificity signals: units, materials, measurable facts.
const SPEC_UNIT_RX =
  /\b\d+(\.\d+)?\s?(cm|mm|m|in|inch|inches|ft|kg|g|lb|lbs|oz|ml|l|litre|liter|gb|tb|mah|w|watt|hz|"|%)\b/gi
const MATERIAL_MARKERS = [
  'cotton', 'wool', 'leather', 'silk', 'linen', 'polyester', 'nylon',
  'aluminium', 'aluminum', 'stainless steel', 'steel', 'titanium', 'oak',
  'walnut', 'bamboo', 'ceramic', 'glass', 'silicone', 'merino', 'cashmere',
  'organic', 'recycled', 'denim', 'suede', 'rubber', 'brass', 'copper',
]

const CTA_MARKERS = [
  'shop', 'buy', 'order', 'add to cart', 'add to bag', 'add yours',
  'get yours', 'grab yours', 'to the cart', 'to your cart', 'try it',
  'subscribe', 'discover', 'browse', 'explore', 'find your', 'pick your',
  'claim', 'choose your', 'treat yourself',
]

// ─── Helpers ───────────────────────────────────────────────────────

function clamp(n: number, lo = 0, hi = 100): number {
  return Math.max(lo, Math.min(hi, n))
}

function splitSentences(text: string): string[] {
  return text
    .replace(/\s+/g, ' ')
    .split(/(?<=[.!?])\s+(?=[A-Z0-9"'(])/)
    .map((s) => s.trim())
    .filter(Boolean)
}

function words(text: string): string[] {
  return text.toLowerCase().match(/[a-z0-9'’%]+/g) || []
}

function countMarkers(lower: string, markers: string[]): number {
  let n = 0
  for (const m of markers) {
    if (lower.includes(m)) n++
  }
  return n
}

function hasBullets(text: string): boolean {
  // Lines starting with a bullet glyph, dash, asterisk, or a digit.
  return /(^|\n)\s*([-*•·]|\d+[.)])\s+\S/.test(text)
}

// ─── Scoring ───────────────────────────────────────────────────────

export function gradeProductDescription(input: string): GraderResult {
  const text = (input || '').trim()
  const lower = text.toLowerCase()
  const wl = words(text)
  const wordCount = wl.length
  const sentences = splitSentences(text)
  const sentenceCount = sentences.length

  const issues: GraderIssue[] = []
  const suggestions: string[] = []

  // 1. Substance / length. A usable PDP description sits roughly in the
  //    40-160 word band. Too short = thin; too long = a wall.
  let substance: number
  if (wordCount === 0) substance = 0
  else if (wordCount < 20) substance = clamp(20 + wordCount * 2)
  else if (wordCount < 40) substance = clamp(55 + (wordCount - 20) * 2)
  else if (wordCount <= 160) substance = 100
  else if (wordCount <= 240) substance = clamp(100 - (wordCount - 160) * 0.4)
  else substance = clamp(70 - (wordCount - 240) * 0.2)
  if (wordCount < 30 && wordCount > 0)
    issues.push({ severity: 'high', text: `Only ${wordCount} words. There is not enough here for a shopper to make a decision, or for an AI engine to quote.` })
  if (wordCount > 240)
    issues.push({ severity: 'medium', text: `${wordCount} words is a wall. Cut to the 40-160 word band and move the detail into a spec list.` })

  // 2. Specificity: measurable specs, units, materials, numbers.
  const unitHits = (text.match(SPEC_UNIT_RX) || []).length
  const materialHits = countMarkers(lower, MATERIAL_MARKERS)
  const numberHits = (text.match(/\b\d+(\.\d+)?\b/g) || []).length
  const specificity = clamp(
    unitHits * 22 + materialHits * 18 + Math.min(numberHits, 4) * 8,
  )
  if (specificity < 40)
    issues.push({ severity: 'high', text: 'Vague on the concrete facts. Add measurable specifics: dimensions, weight, materials, capacity, what is in the box.' })

  // 3. Benefit-vs-feature language.
  const benefitHits = countMarkers(lower, BENEFIT_MARKERS)
  const benefits = clamp(benefitHits * 26)
  if (benefits < 40)
    issues.push({ severity: 'medium', text: 'Reads as a feature list, not a benefit. Translate at least one spec into what the buyer gets ("…so you can…", "…means no more…").' })

  // 4. Sensory + use-case detail.
  const sensoryHits = countMarkers(lower, SENSORY_MARKERS)
  const sensory = clamp(sensoryHits * 20)
  if (sensory < 35)
    issues.push({ severity: 'low', text: 'Little sensory or use-case texture. Say how it feels and where it gets used, so the shopper can picture owning it.' })

  // 5. Trust / social-proof hooks.
  const trustHits = countMarkers(lower, TRUST_MARKERS)
  const trust = clamp(trustHits * 28)
  if (trust === 0)
    issues.push({ severity: 'medium', text: 'No trust hook. A line about reviews, ratings, a guarantee or a return policy resolves the "is this safe to buy" doubt.' })

  // 6. Scannability: bullets + short sentences.
  const bulletsPresent = hasBullets(text)
  const avgWordsPerSentence = sentenceCount ? wordCount / sentenceCount : wordCount
  let scannability = bulletsPresent ? 70 : 35
  if (avgWordsPerSentence <= 18) scannability += 30
  else if (avgWordsPerSentence <= 26) scannability += 12
  scannability = clamp(scannability)
  if (!bulletsPresent && wordCount > 45)
    issues.push({ severity: 'medium', text: 'No bullets. Shoppers scan before they read. Break the key points into 3-5 bullets.' })

  // 7. Readability: penalise long run-on sentences.
  const longSentences = sentences.filter((s) => words(s).length > 32)
  const readability = clamp(100 - longSentences.length * 22 - Math.max(0, avgWordsPerSentence - 20) * 2.5)
  if (longSentences.length > 0)
    issues.push({ severity: longSentences.length > 1 ? 'medium' : 'low', text: `${longSentences.length} run-on sentence${longSentences.length > 1 ? 's' : ''} over 32 words. Split for a cleaner read.` })

  // 8. Filler / AI-tell penalty. Starts at 100 and loses points per hit.
  const fillerFound: string[] = []
  for (const f of FILLER_PATTERNS) {
    const m = text.match(f.rx)
    if (m) fillerFound.push(f.label)
  }
  const fillerCount = fillerFound.length
  const filler = clamp(100 - fillerCount * 24)
  if (fillerCount > 0)
    issues.push({ severity: fillerCount > 2 ? 'high' : 'medium', text: `Filler / cliché phrasing: ${fillerFound.slice(0, 5).join(', ')}${fillerFound.length > 5 ? '…' : ''}. Cut these, they read as generated and prove nothing.` })

  // 9. Clear CTA.
  const ctaHits = countMarkers(lower, CTA_MARKERS)
  const cta = clamp(ctaHits ? 70 + Math.min(ctaHits, 2) * 15 : 0)
  if (ctaHits === 0)
    issues.push({ severity: 'low', text: 'No call to action. Close with a short, direct nudge ("Add yours to the cart", "Find your size").' })

  const dimensions: DimensionScore[] = [
    { id: 'substance',    label: 'Length & substance',     score: Math.round(substance),    weight: 12, verdict: verdictFor('substance', substance, wordCount) },
    { id: 'specificity',  label: 'Specificity',            score: Math.round(specificity),  weight: 18, verdict: verdictFor('specificity', specificity) },
    { id: 'benefits',     label: 'Benefit vs feature',     score: Math.round(benefits),     weight: 15, verdict: verdictFor('benefits', benefits) },
    { id: 'sensory',      label: 'Sensory & use-case',     score: Math.round(sensory),      weight: 10, verdict: verdictFor('sensory', sensory) },
    { id: 'trust',        label: 'Trust & social proof',   score: Math.round(trust),        weight: 12, verdict: verdictFor('trust', trust) },
    { id: 'scannability', label: 'Scannability',           score: Math.round(scannability), weight: 12, verdict: verdictFor('scannability', scannability) },
    { id: 'readability',  label: 'Readability',            score: Math.round(readability),  weight: 9,  verdict: verdictFor('readability', readability) },
    { id: 'filler',       label: 'No filler / AI-tells',   score: Math.round(filler),       weight: 8,  verdict: verdictFor('filler', filler, fillerCount) },
    { id: 'cta',          label: 'Clear call to action',   score: Math.round(cta),          weight: 4,  verdict: verdictFor('cta', cta) },
  ]

  const totalWeight = dimensions.reduce((a, d) => a + d.weight, 0)
  const weighted = dimensions.reduce((a, d) => a + d.score * d.weight, 0) / totalWeight
  const score = wordCount === 0 ? 0 : Math.round(clamp(weighted))

  const band: GraderResult['band'] =
    score >= 80 ? 'strong' : score >= 60 ? 'good' : score >= 40 ? 'fair' : 'weak'

  // Suggestions: the lowest-scoring dimensions, in priority order.
  const ranked = [...dimensions].sort((a, b) => a.score - b.score)
  for (const d of ranked.slice(0, 3)) {
    if (d.score < 75) suggestions.push(suggestionFor(d.id))
  }
  if (suggestions.length === 0)
    suggestions.push('Strong across the board. The biggest remaining lift is a real verified-review block and a product video, neither of which a description alone can carry.')

  return {
    score,
    band,
    wordCount,
    sentenceCount,
    dimensions,
    issues: issues.sort((a, b) => sev(b.severity) - sev(a.severity)),
    suggestions,
    rewrite: buildRewrite(text, sentences, { trustHits, ctaHits, specUnits: unitHits, materialHits }),
  }
}

function sev(s: GraderIssue['severity']): number {
  return s === 'high' ? 3 : s === 'medium' ? 2 : 1
}

function verdictFor(id: DimensionId, score: number, extra?: number): string {
  const good = score >= 75
  const mid = score >= 45
  switch (id) {
    case 'substance':
      if (score >= 75) return `Right depth (${extra} words).`
      if ((extra ?? 0) === 0) return 'Empty.'
      if ((extra ?? 0) < 40) return `Thin (${extra} words). Add detail.`
      return `Long (${extra} words). Trim it.`
    case 'specificity':
      return good ? 'Concrete specs present.' : mid ? 'Some specifics, add more.' : 'Too vague, no measurable facts.'
    case 'benefits':
      return good ? 'Translates specs into outcomes.' : mid ? 'A few benefits, lead with more.' : 'Feature list, no payoff for the buyer.'
    case 'sensory':
      return good ? 'Easy to picture owning.' : mid ? 'Some texture.' : 'No feel or use-case.'
    case 'trust':
      return good ? 'Trust hooks present.' : mid ? 'One trust signal, add another.' : 'No proof or reassurance.'
    case 'scannability':
      return good ? 'Scans well.' : mid ? 'Partly scannable.' : 'A block of prose, hard to scan.'
    case 'readability':
      return good ? 'Clean, short sentences.' : mid ? 'A little dense.' : 'Run-on sentences.'
    case 'filler':
      return (extra ?? 0) === 0 ? 'Clean, no clichés.' : `${extra} filler phrase${(extra ?? 0) > 1 ? 's' : ''} to cut.`
    case 'cta':
      return good ? 'Clear nudge to buy.' : 'No call to action.'
  }
}

function suggestionFor(id: DimensionId): string {
  switch (id) {
    case 'substance': return 'Aim for the 40-160 word sweet spot: enough to decide, short enough to read.'
    case 'specificity': return 'Add the measurable facts a buyer screens on: size, weight, material, capacity, what is included.'
    case 'benefits': return 'Pair each headline feature with its payoff: "double-stitched seams, so it survives the wash for years".'
    case 'sensory': return 'Add one line of how it feels and where it is used, so the shopper pictures owning it.'
    case 'trust': return 'Add a single trust line: rating, review count, guarantee or return window.'
    case 'scannability': return 'Break the body into 3-5 benefit-led bullets so it can be scanned in two seconds.'
    case 'readability': return 'Split any sentence over ~25 words. One idea per sentence reads faster.'
    case 'filler': return 'Delete the cliché adjectives ("seamless", "premium quality", "game-changer") and replace each with a fact.'
    case 'cta': return 'End with one direct action: "Add yours to the cart" or "Find your size".'
  }
}

// ─── Rewrite scaffold ──────────────────────────────────────────────
// Deterministic restructure of the writer's OWN text into a clean PDP
// shape. It strips filler, splits run-on sentences and reorders the
// content. It never invents facts: spec/trust/CTA lines fall back to
// neutral prompts the writer fills in, clearly bracketed.

function stripFiller(s: string): string {
  let out = s
  for (const f of FILLER_PATTERNS) out = out.replace(f.rx, '')
  return out.replace(/\s{2,}/g, ' ').replace(/\s+([.,!?])/g, '$1').trim()
}

function splitRunOns(s: string): string[] {
  // Break a long sentence at "and"/"but"/", which"/", so" boundaries.
  if (words(s).length <= 26) return [s]
  const parts = s.split(/\s*(?:,\s*(?:and|but|so|which|that)\b|\b;\b)\s*/i).map((p) => p.trim()).filter(Boolean)
  return parts.length > 1 ? parts : [s]
}

function titleCaseFirst(s: string): string {
  const t = s.trim().replace(/^[-*•·]\s*/, '')
  return t ? t[0].toUpperCase() + t.slice(1) : t
}

function buildRewrite(
  text: string,
  sentences: string[],
  ctx: { trustHits: number; ctaHits: number; specUnits: number; materialHits: number },
): RewriteScaffold {
  const cleaned = sentences.map(stripFiller).filter(Boolean)

  // Hook: the first cleaned sentence, tightened.
  const hook = cleaned.length
    ? titleCaseFirst(splitRunOns(cleaned[0])[0])
    : '[Open with the one thing that makes this product worth a look.]'

  // Bullets: subsequent sentences, split at run-on boundaries, deduped,
  // capped at five. These are the writer's own points, restructured.
  const bulletPool: string[] = []
  for (const s of cleaned.slice(1)) {
    for (const part of splitRunOns(s)) {
      const b = titleCaseFirst(part).replace(/[.]+$/, '')
      if (b.length >= 8 && !bulletPool.includes(b)) bulletPool.push(b)
    }
  }
  const bullets = bulletPool.slice(0, 5)
  if (bullets.length === 0) {
    bullets.push('[Benefit one: what the buyer gets, not just a feature.]')
    bullets.push('[Benefit two: the spec that backs it up.]')
    bullets.push('[Benefit three: where and how it is used.]')
  }

  // Spec block: pull lines that carry a unit / measurable fact.
  const specs: string[] = []
  for (const s of cleaned) {
    if (SPEC_UNIT_RX.test(s) || /\b\d/.test(s)) {
      SPEC_UNIT_RX.lastIndex = 0
      const spec = titleCaseFirst(splitRunOns(s)[0]).replace(/[.]+$/, '')
      if (spec.length >= 6 && !specs.includes(spec)) specs.push(spec)
    }
    SPEC_UNIT_RX.lastIndex = 0
  }
  if (specs.length === 0)
    specs.push('[Add the hard specs: size, weight, material, capacity, what is in the box.]')

  const trustLine =
    ctx.trustHits > 0
      ? 'Carry your real proof here: rating, review count, guarantee or return window.'
      : '[Add a trust line: star rating, number of reviews, or your returns / guarantee policy.]'

  const cta = ctx.ctaHits > 0 ? 'Add yours to the cart.' : '[Close with one clear action, e.g. "Add yours to the cart" or "Find your size".]'

  return { hook, bullets, specs: specs.slice(0, 6), trustLine, cta }
}
